import React, { useEffect, useState } from 'react';
import { CheckCircle2, Package, Mail, Loader2, AlertCircle, Home } from 'lucide-react';
import { amplifyApi } from '../services/amplifyApi';

const OrderSuccessPage = () => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      // Get session_id from URL params
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');

      if (!sessionId) {
        setError('No session ID found');
        setLoading(false);
        return;
      }

      try {
        const response = await amplifyApi.getOrderBySession(sessionId);
        if (response.success) {
          setOrder(response.order);
        } else {
          setError(response.error || 'Failed to load order details');
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
          <p className="text-slate-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center space-y-6">
          <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle size={32} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Order Status Unclear</h2>
            <p className="text-slate-600">{error}</p>
            <p className="text-slate-500 text-sm mt-2">
              If you completed payment, you should receive a confirmation email shortly.
            </p>
          </div>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Home className="w-5 h-5" />
            Return Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-lg w-full space-y-8">
        {/* Success Icon */}
        <div className="text-center">
          <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={40} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Order Confirmed!</h1>
          <p className="text-slate-600 mt-2">Thank you for your purchase</p>
        </div>

        {/* Order Details */}
        {order && (
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Order ID</span>
                <span className="font-mono text-sm text-slate-900">{order.orderId}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Status</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                  <Package className="w-4 h-4" />
                  {order.status === 'payment_completed' ? 'Processing' : order.status}
                </span>
              </div>
              {order.totalAmount && (
                <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                  <span className="text-slate-900 font-semibold">Total Paid</span>
                  <span className="text-lg font-bold text-slate-900">
                    ${(order.totalAmount / 100).toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            {/* Email Notice */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl">
              <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-blue-900 font-medium">Confirmation email sent</p>
                <p className="text-sm text-blue-700 mt-1">
                  We've sent order details to <strong>{order.customerEmail}</strong>
                </p>
              </div>
            </div>

            {/* Shipping Info */}
            {order.shippingAddress && (
              <div className="p-4 border border-slate-200 rounded-xl">
                <h3 className="text-sm font-semibold text-slate-900 mb-2">Shipping To</h3>
                <p className="text-sm text-slate-600">
                  {order.shippingAddress.first_name} {order.shippingAddress.last_name}<br />
                  {order.shippingAddress.address1}<br />
                  {order.shippingAddress.address2 && <>{order.shippingAddress.address2}<br /></>}
                  {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zip}<br />
                  {order.shippingAddress.country}
                </p>
              </div>
            )}
          </div>
        )}

        {/* What's Next */}
        <div className="space-y-3 text-center">
          <h3 className="text-sm font-semibold text-slate-900">What happens next?</h3>
          <ol className="text-sm text-slate-600 space-y-2 text-left">
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
              <span>Your order is being prepared for production</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
              <span>You'll receive tracking info once it ships (usually 3-5 business days)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
              <span>Your custom product will arrive at your doorstep!</span>
            </li>
          </ol>
        </div>

        {/* Return Home Button */}
        <div className="text-center pt-4">
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition-colors"
          >
            <Home className="w-5 h-5" />
            Continue Shopping
          </a>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
